import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import DialogModal from 'tradera-components/dialog/dialog-modal';
import ModalContainer from 'static/script/components/modals/modal-container';
import ModalHeader from 'static/script/components/modals/modal-header';
import ModalBody from 'static/script/components/modals/modal-body';
import { useTranslator } from 'tradera-lang/translate';
import dropin from 'braintree-web-drop-in';
import { webApiClient } from 'tradera-utils/http';
import { setIsInternationalWacko } from 'tradera-state/views/view-item/reducer';
import './card-verification-modal.scss';
import { LocalLoader } from 'tradera-components/loader';
import { selectPreferredLanguage } from 'tradera-state/language/selectors';
import {
    openQualtiveModal,
    surveys
} from 'static/script/components/customer-feedback/qualtive-loader';
import GtmService from 'static/script/app/ui/google-tagmanager-service';

const states = {
    START: 'START',
    LOADING: 'LOADING',
    CARD_VERIFICATION_MODAL: 'CARD_VERIFICATION_MODAL',
    ERROR: 'ERROR',
    NOT_VERIFIED: 'NOT_VERIFIED'
};

export const CardVerificationModal = ({ closeModal, onCardVerified }) => {
    const preferredLanguage = useSelector(selectPreferredLanguage);
    const memberEmail = useSelector(state => state.initData.memberEmail);
    const [state, setState] = useState(states.START);
    const [isHidden, setIsHidden] = useState(false);
    const { t } = useTranslator();
    const submitButtonRef = useRef();
    const dispatch = useDispatch();

    const onClose = () => {
        closeModal();
        GtmService.trackAction('VIP', 'Card Verification', 'Close modal');
        openQualtiveModal(surveys.cardVerification, t);
    };

    useEffect(() => {
        if (state !== states.START) {
            return;
        }

        GtmService.trackAction('VIP', 'Card Verification', 'Start');

        const onSuccess = nonce => {
            setState(states.LOADING);
            webApiClient
                .post('/shopping/payment/verifybraintreecardnonce', { nonce })
                .then(({ data }) => {
                    if (data.isVerified) {
                        onCardVerified();
                        GtmService.trackAction(
                            'VIP',
                            'Card Verification',
                            'Verified'
                        );
                        dispatch(setIsInternationalWacko(false));
                        return;
                    }
                    GtmService.trackAction(
                        'VIP',
                        'Card Verification',
                        'NotVerified'
                    );
                    setIsHidden(false);
                    setState(states.NOT_VERIFIED);
                    return;
                })
                .catch(() => {
                    GtmService.trackAction(
                        'VIP',
                        'Card Verification',
                        'Server error'
                    );
                    setState(states.ERROR);
                });
        };

        webApiClient
            .get('/shopping/payment/braintreeclienttokenforverification')
            .then(({ data }) => {
                setState(states.CARD_VERIFICATION_MODAL);
                const options = {
                    authorization: data.clientToken,
                    container: '#dropin-container',
                    locale: preferredLanguage.languageCodeIso2,
                    threeDSecure: true
                };
                dropin.create(options, (createErrors, instance) => {
                    submitButtonRef.current.addEventListener('click', () => {
                        setIsHidden(true);
                        try {
                            instance.requestPaymentMethod(
                                {
                                    threeDSecure: {
                                        amount: '1',
                                        email: memberEmail
                                    }
                                },
                                (requestPaymentMethodErr, payload) => {
                                    if (requestPaymentMethodErr) {
                                        setIsHidden(false);
                                        setState(states.NOT_VERIFIED);
                                        const error =
                                            requestPaymentMethodErr._braintreeWebError ||
                                            {};

                                        GtmService.trackAction(
                                            'VIP',
                                            'Card Verification',
                                            `${error.type}:${error.code}`
                                        );

                                        return;
                                    }

                                    if (!payload || !payload.nonce) {
                                        setIsHidden(false);
                                        setState(states.NOT_VERIFIED);

                                        if (payload) {
                                            const threeDSecureValidationFailed =
                                                payload.liabilityShiftPossible &&
                                                !payload.liabilityShifted;

                                            GtmService.trackAction(
                                                'VIP',
                                                'Card Verification',
                                                threeDSecureValidationFailed
                                                    ? 'ThreeDSecure failed'
                                                    : 'Card invalid'
                                            );
                                        }
                                        return;
                                    }

                                    onSuccess(payload.nonce);
                                }
                            );
                        } catch (e) {
                            setIsHidden(false);
                            setState(states.NOT_VERIFIED);
                            GtmService.trackAction(
                                'VIP',
                                'Card Verification',
                                'requestPaymentMethod failed'
                            );
                            throw e;
                        }
                    });
                });
                return;
            })
            .catch(() => setState(states.ERROR));
        setState(states.LOADING);
    }, [onCardVerified, dispatch, state, preferredLanguage, memberEmail]);

    const restart = () => {
        GtmService.trackAction('VIP', 'Card Verification', 'Restart');
        setState(states.START);
    };

    return (
        <DialogModal
            onDismiss={onClose}
            className={'card-verification-modal' + (isHidden ? ' d-none' : '')}>
            <ModalContainer onClose={onClose}>
                <ModalHeader className="border-bottom mt-0" onClose={onClose}>
                    <div>
                        {t('internationalwacko_cardverificationmodal_header')}
                    </div>
                </ModalHeader>
                <ModalBody className="justify-content-center position-relative">
                    {state === states.LOADING && <LocalLoader />}
                    {state === states.CARD_VERIFICATION_MODAL && (
                        <div className="w-100 h-100 p-5">
                            <h4 className="mb-4 text-center">
                                {t(
                                    'internationalwacko_cardverificationmodal_introtext'
                                )}
                            </h4>
                            <p className="mb-2">
                                {t(
                                    'internationalwacko_cardverificationmodal_info1'
                                )}
                            </p>
                            <p className="mb-4">
                                {t(
                                    'internationalwacko_cardverificationmodal_info2'
                                )}
                            </p>

                            <div id="dropin-container" className="mb-4" />

                            <button
                                className="btn btn-primary btn-fluid"
                                ref={submitButtonRef}>
                                {t(
                                    'internationalwacko_cardverificationmodal_verifyCard'
                                )}
                            </button>
                        </div>
                    )}
                    {state === states.ERROR && (
                        <p className="text-center text-danger p-5">
                            {t(
                                'internationalwacko_cardverificationmodal_unavailable'
                            )}
                        </p>
                    )}
                    {state === states.NOT_VERIFIED && (
                        <div className="text-center p-5">
                            <h4 className="my-4">
                                {t(
                                    'internationalwacko_cardverificationmodal_failedVerification'
                                )}
                            </h4>
                            <p className="mb-2">
                                {t(
                                    'internationalwacko_cardverificationmodal_failedVerification_info'
                                )}
                            </p>
                            <button
                                className="btn btn-primary btn-fluid"
                                onClick={restart}>
                                {t(
                                    'internationalwacko_cardverificationmodal_tryAgain'
                                )}
                            </button>
                        </div>
                    )}
                </ModalBody>
            </ModalContainer>
        </DialogModal>
    );
};
